import { useState, useEffect } from "react";
import styled from "styled-components";
import { usePage } from "@inertiajs/react";
import Lightbox from "./Lightbox";
import {
  Container,
  SectionHeader,
  Subtitle,
  Title,
  Description,
} from "./Section";
import { useLandingLanguage } from "../../../contexts/landing-language";

const GallerySection = styled.section`
  padding: 120px 24px;
  background: linear-gradient(180deg, #f5f2ed 0%, #faf8f5 100%);

  @media (max-width: 768px) {
    padding: 80px 24px;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryCell = styled.div`
  grid-column: ${(props) => (props.$isBig ? "span 2" : "span 1")};
  grid-row: ${(props) => (props.$isBig ? "span 2" : "span 1")};
  position: relative;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 1;

  @media (max-width: 768px) {
    grid-column: span 1;
    grid-row: span 1;
  }
`;

const GalleryCellInner = styled.div`
  width: 100%;
  height: 100%;
  background-image: ${(props) =>
    props.$image ? `url(${props.$image})` : "none"};
  background-color: ${(props) => (!props.$image ? "#e5e1dc" : "transparent")};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      transparent 40%,
      rgba(26, 26, 26, 0.8) 100%
    );
    opacity: 0.7;
    transition: opacity 0.4s;
  }

  ${GalleryCell}:hover & {
    transform: scale(1.02);

    &::before {
      opacity: 0.9;
    }
  }
`;

const GalleryLabel = styled.span`
  position: relative;
  z-index: 1;
  padding: 20px;
  font-size: 14px;
  color: #faf8f5;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const GalleryItemCard = ({ item, onClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = item.image_urls || [];

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((i) => (i + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  const handleClick = () => {
    onClick(currentIndex);
  };

  return (
    <GalleryCell $isBig={item.is_big} onClick={handleClick}>
      <GalleryCellInner $image={images[currentIndex]}>
        <GalleryLabel>{item.name}</GalleryLabel>
      </GalleryCellInner>
    </GalleryCell>
  );
};

const Gallery = () => {
  const { galleries } = usePage().props;
  const { t } = useLandingLanguage();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Flatten all images from all gallery items into a single list
  const allImages = (galleries || []).flatMap((item) => item.image_urls || []);

  // Calculate the global index offset for each gallery item
  const getGlobalIndex = (itemIndex, imgIndex) => {
    let offset = 0;
    for (let i = 0; i < itemIndex; i++) {
      offset += (galleries[i].image_urls || []).length;
    }
    return offset + imgIndex;
  };

  return (
    <GallerySection id="gallery">
      <Container>
        <SectionHeader>
          <Subtitle>{t.gallery.subtitle}</Subtitle>
          <Title>{t.gallery.title}</Title>
          <Description>{t.gallery.description}</Description>
        </SectionHeader>

        <GalleryGrid>
          {(galleries || []).map((item, itemIndex) => (
            <GalleryItemCard
              key={itemIndex}
              item={item}
              onClick={(imgIndex) =>
                setLightboxIndex(getGlobalIndex(itemIndex, imgIndex))
              }
            />
          ))}
        </GalleryGrid>
      </Container>

      {lightboxIndex !== null && (
        <Lightbox
          images={allImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </GallerySection>
  );
};

export default Gallery;
